import * as Sentry from '@sentry/node'
import { Integrations } from '@sentry/tracing'
import getConfig from 'next/config'

const toSentryUser = (user: { userId: string; email: string }) => {
  if (!user) {
    return null
  }

  return {
    id: user.userId,
    email: user.email,
  }
}

export const setSentryUser = (user: { userId: string; email: string }) => {
  if (user) {
    Sentry.setUser(toSentryUser(user))
  }
}

/**
 * @description Initializes Sentry with the DSN, release =ID and extra config.
 * This function is modified from https://github.com/zeit/next.js/blob/canary/examples/with-sentry/utils/sentry.js
 * Modified to get vaiables from next's getCofig rather than directly from node.
 */
export const initSentry = (user?: { userId: string; email: string }) => {
  const { publicRuntimeConfig } = getConfig()
  const { NODE_ENV, CGC_WEB_RELEASE_ID, CGC_WEB_SENTRY_DSN } = publicRuntimeConfig

  const sentryOptions = {
    dsn: CGC_WEB_SENTRY_DSN,
    release: CGC_WEB_RELEASE_ID,
    maxBreadcrumbs: 50,
    attachStacktrace: true,
    environment: NODE_ENV,
    integrations: [],
    tracesSampleRate: 0.01,
    ignoreErrors: [
      // Random plugins/extensions
      'top.GLOBALS',
      // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'http://tt.epicplay.com',
      "Can't find variable: ZiteReader",
      'jigsaw is not defined',
      'ComboSearch is not defined',
      'http://loading.retry.widdit.com/',
      'atomicFindClose',
      // Facebook borked
      'fb_xd_fragment',
      // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
      // reduce this. (thanks @acdha)
      // See http://stackoverflow.com/questions/4113268
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
      'conduitPage',
      'pubads',
    ],
    denyUrls: [
      // Facebook flakiness
      /graph\.facebook\.com/i,
      // Facebook blocked
      /connect\.facebook\.net\/en_US\/all\.js/i,
      // Woopra flakiness
      /eatdifferent\.com\.woopra-ns\.com/i,
      /static\.woopra\.com\/js\/woopra\.js/i,
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      // Other plugins
      /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
      /webappstoolbarba\.texthelp\.com\//i,
      /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
      // Google ads
      /doubleclick\.net/i,
      /google-analytics\.com/i,
      /googlesyndication\.com/i,
    ],
  }

  const isBrowser = !!process.browser
  if (isBrowser) {
    // @ts-ignore
    sentryOptions.integrations.push(new Integrations.BrowserTracing())
  }

  Sentry.init(sentryOptions)
  Sentry.configureScope((scope) => {
    scope.setTag('isBrowser', isBrowser)
    if (user) {
      scope.setUser(toSentryUser(user))
    }
  })
}

export const captureException = (err: TODO, ctx: TODO = null) => {
  console.error(err)

  const isBrowser = !!process.browser
  const extra: { [key: string]: any } = {
    ssr: !isBrowser,
  }
  if (err?.statusCode) {
    extra.statusCode = err.statusCode
  }
  if (err?.bodyText) {
    extra.bodyText = err.bodyText
  }
  if (err?.stack) {
    extra.stack = err.stack
  }

  if (isBrowser) {
    extra.query = window.location.search
    extra.pathname = window.location.pathname
  }

  const networkError = err?.networkError
  if (networkError) {
    extra.networkErrorName = networkError.name
    extra.networkErrorStatusCode = networkError.statusCode
    extra.bodyText = networkError.bodyText
    extra.stack = networkError.stack
  }

  if (ctx) {
    const { req, res, errorInfo } = ctx

    if (res && res.statusCode) {
      extra.statusCode = res.statusCode
    }

    if (req) {
      extra.url = req.url
      extra.method = req.method
      extra.headers = req.headers
      extra.params = req.params
      extra.query = req.query
    }

    if (errorInfo) {
      Object.keys(errorInfo).forEach((key) => {
        extra[`errorInfo${key}`] = errorInfo[key]
      })
    }
  }

  Sentry.captureException(err, { extra: extra })
}
